import { v4 as uuidv4 } from 'uuid';
import { CardState, Position } from '@/types/game';

// Default card back image - an MTG card back
export const DEFAULT_CARD_BACK = "https://gatherer.wizards.com/Handlers/Image.ashx?type=card&multiverseid=0";

/**
 * Load a card from a given URL
 * @param imageUrl The URL of the card's front image
 * @param backImageUrl Optional URL for the card's back image
 * @param position Position to place the card
 * @param name Optional name for the card
 * @returns Promise with the created CardState
 */
export async function loadCardFromUrl(
  imageUrl: string,
  backImageUrl?: string,
  position?: Position,
  name?: string
): Promise<CardState> {
  try {
    // Try to check if the URL is valid
    await validateImageUrl(imageUrl);
  } catch (error) {
    console.error("Error loading card image:", error);
  }

  // Create the card state
  return {
    id: uuidv4(),
    position: position || { x: 0, y: 0 },
    rotation: 0,
    isFaceUp: true,
    imageUrl: imageUrl,
    backImageUrl: backImageUrl || DEFAULT_CARD_BACK,
    name: name || 'Card',
    counters: {},
  };
}

/**
 * Validate an image URL by attempting to load it
 * @param url The URL to validate
 * @returns Promise that resolves if valid, rejects if invalid
 */
function validateImageUrl(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => resolve();
    img.onerror = () => reject(new Error(`Failed to load image from URL: ${url}`));
    
    img.src = url;
  });
}

/**
 * Parse a deck list and create cards
 * @param deckList Text containing card data (name, quantity, set)
 * @returns Array of CardState objects
 */
export function parseDeckList(deckList: string): CardState[] {
  const cards: CardState[] = [];
  const lines = deckList.split('\n');
  
  const startPosition = { x: 0, y: 0 };
  let offset = 0;
  
  for (const line of lines) {
    // Skip empty lines
    if (!line.trim()) continue;
    
    // Try to parse the line (quantity x cardname [set])
    const match = line.match(/(\d+)\s+(?:x\s+)?(.+?)(?:\s+\[(.+?)\])?$/);
    
    if (match) {
      const [, quantityStr, cardName, set] = match;
      const quantity = parseInt(quantityStr, 10) || 1;
      
      // For each copy of the card
      for (let i = 0; i < quantity; i++) {
        // Use Scryfall API format by default
        let imageUrl = `https://api.scryfall.com/cards/named?exact=${encodeURIComponent(cardName)}`;
        if (set) {
          imageUrl += `&set=${encodeURIComponent(set)}`;
        }
        
        cards.push({
          id: uuidv4(),
          position: {
            x: startPosition.x + offset,
            y: startPosition.y + offset,
          },
          rotation: 0,
          isFaceUp: true,
          imageUrl,
          backImageUrl: DEFAULT_CARD_BACK,
          name: cardName,
          counters: {},
        });
        
        // Increment offset for staggered positioning
        offset += 2;
      }
    }
  }
  
  return cards;
} 
